<template>
  <div class="injury-trend-chart-container">
    <!-- Chart Controls -->
    <div class="chart-controls">
      <div class="control-row">
        <div class="view-controls">
          <label>Time Period:</label>
          <button v-for="view in viewOptions" :key="view.value" @click="currentView = view.value"
            :class="{ active: currentView === view.value }" class="view-btn">
            {{ view.label }}
          </button>
        </div>

        <div class="filter-controls">
          <label>Severity Filter:</label>
          <select v-model="selectedSeverity" @change="updateChart">
            <option value="all">All Severities</option>
            <option value="minor">Minor</option>
            <option value="moderate">Moderate</option>
            <option value="severe">Severe</option>
          </select>
        </div>
      </div>

      <div class="control-row">
        <div class="chart-type-controls">
          <label>Chart Type:</label>
          <button v-for="type in chartTypeOptions" :key="type.value" @click="currentChartType = type.value"
            :class="{ active: currentChartType === type.value }" class="chart-type-btn">
            {{ type.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Chart -->
    <div class="chart-wrapper">
      <div v-if="!hasData" class="no-data-message">
        <p>No injury data available for the selected period and filters.</p>
      </div>
      <component v-else :is="chartComponent" :chart-options="chartOptions" :chart-data="chartData" :chart-id="chartId"
        :style="myStyles" role="img" :aria-label="chartAriaLabel" />
    </div>

    <!-- Chart Summary -->
    <div class="chart-summary">
      <div class="summary-stats">
        <div class="stat-item">
          <span class="stat-label">Total Injuries:</span>
          <span class="stat-value">{{ totalInjuries }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Average per {{ currentView }}:</span>
          <span class="stat-value">{{ averagePerPeriod }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Trend:</span>
          <span class="stat-value" :class="trendClass">{{
            trendIndicator
            }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Line, Bar, Doughnut } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  ArcElement,
} from "chart.js";
import "chartjs-adapter-date-fns";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  ArcElement,
);

export default {
  name: "InjuryTrendChart",
  components: {
    LineChart: Line,
    BarChart: Bar,
    DoughnutChart: Doughnut,
  },
  props: {
    injuries: { type: Array, required: true },
    dateRange: { type: Array, required: true },
    chartId: { type: String, default: "injury-trend-chart" },
    viewMode: { type: String, default: "week" },
  },
  data() {
    return {
      currentView: "week",
      selectedSeverity: "all",
      currentChartType: "stacked-bar",
      viewOptions: [
        { value: "day", label: "Daily" },
        { value: "week", label: "Weekly" },
        { value: "month", label: "Monthly" },
      ],
      chartTypeOptions: [
        { value: "stacked-bar", label: "Stacked Bar" },
        { value: "line", label: "Line Chart" },
        { value: "total-bar", label: "Total Bar" },
        { value: "doughnut", label: "By Type" },
      ],
    };
  },
  computed: {
    chartComponent() {
      switch (this.currentChartType) {
        case "line":
          return "LineChart";
        case "stacked-bar":
        case "total-bar":
          return "BarChart";
        case "doughnut":
          return "DoughnutChart";
        default:
          return "BarChart";
      }
    },
    chartData() {
      switch (this.currentChartType) {
        case "line":
          return this.processTimeSeriesData();
        case "stacked-bar":
          return this.processStackedBarData();
        case "total-bar":
          return this.processTotalBarData();
        case "doughnut":
          return this.processDoughnutData();
        default:
          return this.processStackedBarData();
      }
    },
    chartOptions() {
      switch (this.currentChartType) {
        case "line":
          return this.lineChartOptions;
        case "stacked-bar":
          return this.stackedBarChartOptions;
        case "total-bar":
          return this.totalBarChartOptions;
        case "doughnut":
          return this.doughnutChartOptions;
        default:
          return this.stackedBarChartOptions;
      }
    },
    myStyles() {
      return {
        height: "400px",
        width: "100%",
      };
    },
    filteredInjuries() {
      if (this.selectedSeverity === "all") {
        return this.injuries;
      }
      return this.injuries.filter(
        (injury) =>
          injury.severity &&
          injury.severity.toLowerCase() === this.selectedSeverity.toLowerCase(),
      );
    },
    totalInjuries() {
      return this.filteredInjuries.length;
    },
    averagePerPeriod() {
      if (this.totalInjuries === 0) return 0;

      const dateRange = this.getDateRange();
      const periods = this.calculatePeriods(dateRange);

      return periods > 0
        ? Math.round((this.totalInjuries / periods) * 10) / 10
        : 0;
    },
    trendIndicator() {
      const trend = this.calculateTrend();
      if (trend > 0.1) return "↗ Increasing";
      if (trend < -0.1) return "↘ Decreasing";
      return "→ Stable";
    },
    trendClass() {
      const trend = this.calculateTrend();
      if (trend > 0.1) return "trend-up";
      if (trend < -0.1) return "trend-down";
      return "trend-stable";
    },
    hasData() {
      return this.filteredInjuries.length > 0;
    },
    isLoading() {
      return false;
    },
    chartAriaLabel() {
      return `Injury trend chart showing ${this.totalInjuries} injuries over time, filtered by ${this.selectedSeverity} severity. Current view: ${this.currentView}ly. Trend: ${this.trendIndicator}`;
    },
    lineChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const element = elements[0];
            const datasetIndex = element.datasetIndex;
            const dataIndex = element.index;
            this.handleDrillDown(datasetIndex, dataIndex);
          }
        },
        plugins: {
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              padding: 20,
            },
          },
          tooltip: {
            callbacks: {
              title: (context) => {
                const periodLabel =
                  this.currentView === "day"
                    ? "Day"
                    : this.currentView === "week"
                      ? "Week of"
                      : "Month of";
                return `${periodLabel} ${context[0].label}`;
              },
              label: (context) => {
                const injuryLabel =
                  context.parsed.y === 1 ? "injury" : "injuries";
                return `${context.dataset.label}: ${context.parsed.y} ${injuryLabel}`;
              },
            },
          },
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit: this.currentView,
              displayFormats: {
                day: "MMM dd",
                week: "MMM dd",
                month: "MMM yyyy",
              },
            },
            title: {
              display: true,
              text: "Time Period",
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Number of Injuries",
            },
            ticks: {
              stepSize: 1,
            },
          },
        },
      };
    },
    stackedBarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const element = elements[0];
            const datasetIndex = element.datasetIndex;
            const dataIndex = element.index;
            this.handleStackedBarDrillDown(datasetIndex, dataIndex);
          }
        },
        plugins: {
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              padding: 15,
            },
          },
          tooltip: {
            callbacks: {
              title: (context) => {
                const periodLabel =
                  this.currentView === "day"
                    ? "Day"
                    : this.currentView === "week"
                      ? "Week of"
                      : "Month of";
                return `${periodLabel} ${context[0].label}`;
              },
              label: (context) => {
                const injuryLabel =
                  context.parsed.y === 1 ? "injury" : "injuries";
                return `${context.dataset.label}: ${context.parsed.y} ${injuryLabel}`;
              },
              afterBody: (context) => {
                const total = context.reduce(
                  (sum, item) => sum + item.parsed.y,
                  0,
                );
                return `Total: ${total} ${total === 1 ? "injury" : "injuries"}`;
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            title: {
              display: true,
              text: "Time Period",
            },
          },
          y: {
            beginAtZero: true,
            stacked: true,
            title: {
              display: true,
              text: "Number of Injuries",
            },
            ticks: {
              stepSize: 1,
            },
          },
        },
      };
    },
    totalBarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const element = elements[0];
            const dataIndex = element.index;
            this.handleTotalBarDrillDown(dataIndex);
          }
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title: (context) => {
                const periodLabel =
                  this.currentView === "day"
                    ? "Day"
                    : this.currentView === "week"
                      ? "Week of"
                      : "Month of";
                return `${periodLabel} ${context[0].label}`;
              },
              label: (context) => {
                const injuryLabel =
                  context.parsed.y === 1 ? "injury" : "injuries";
                return `Total: ${context.parsed.y} ${injuryLabel}`;
              },
            },
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Time Period",
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Total Injuries",
            },
            ticks: {
              stepSize: 1,
            },
          },
        },
      };
    },
    doughnutChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const element = elements[0];
            const dataIndex = element.index;
            this.handleDoughnutDrillDown(dataIndex);
          }
        },
        plugins: {
          legend: {
            position: "right",
            labels: {
              usePointStyle: true,
              padding: 20,
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const total = context.dataset.data.reduce(
                  (sum, val) => sum + val,
                  0,
                );
                const percentage = ((context.parsed / total) * 100).toFixed(1);
                const injuryLabel =
                  context.parsed === 1 ? "injury" : "injuries";
                return `${context.label}: ${context.parsed} ${injuryLabel} (${percentage}%)`;
              },
            },
          },
        },
      };
    },
  },
  watch: {
    currentView() {
      this.updateChart();
      this.$emit("view-mode-change", this.currentView);
    },
    selectedSeverity() {
      this.updateChart();
    },
    currentChartType() {
      this.updateChart();
    },
    viewMode: {
      handler(newViewMode) {
        if (newViewMode && newViewMode !== this.currentView) {
          this.currentView = newViewMode;
        }
      },
      immediate: true,
    },
  },
  methods: {
    updateChart() {
      this.$forceUpdate();
    },

    getDateRange() {
      if (this.dateRange && this.dateRange.length >= 2) {
        return {
          start: new Date(this.dateRange[0]),
          end: new Date(this.dateRange[1]),
        };
      }

      const dates = this.filteredInjuries.map(
        (injury) => new Date(injury.created_date),
      );
      return {
        start: new Date(Math.min(...dates)),
        end: new Date(Math.max(...dates)),
      };
    },

    calculatePeriods(dateRange) {
      const timeDiff = dateRange.end - dateRange.start;
      const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

      switch (this.currentView) {
        case "day":
          return daysDiff;
        case "week":
          return Math.ceil(daysDiff / 7);
        case "month":
          return Math.ceil(daysDiff / 30);
        default:
          return 1;
      }
    },

    calculateTrend() {
      const timeSeriesData = this.processTimeSeriesData();
      if (!timeSeriesData.datasets || timeSeriesData.datasets.length === 0) {
        return 0;
      }

      const allDataPoints = timeSeriesData.datasets
        .flatMap((dataset) => dataset.data)
        .map((point) => point.y);

      if (allDataPoints.length < 2) return 0;

      const midpoint = Math.floor(allDataPoints.length / 2);
      const firstHalf = allDataPoints.slice(0, midpoint);
      const secondHalf = allDataPoints.slice(midpoint);

      const firstAvg =
        firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
      const secondAvg =
        secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;

      return (secondAvg - firstAvg) / (firstAvg || 1);
    },

    processTimeSeriesData() {
      const periodData = {};
      const injuryTypes = new Set();

      this.filteredInjuries.forEach((injury) => {
        const period = this.getPeriodKey(injury.created_date);
        const type = this.mapInjuryType(injury.injury_type);

        if (!periodData[period]) {
          periodData[period] = {};
        }
        if (!periodData[period][type]) {
          periodData[period][type] = 0;
        }
        periodData[period][type]++;
        injuryTypes.add(type);
      });

      const periods = Object.keys(periodData).sort();
      const datasets = Array.from(injuryTypes).map((type) => ({
        label: type,
        data: periods.map((period) => ({
          x: period,
          y: periodData[period][type] || 0,
        })),
        borderColor: this.getColorForInjuryType(type),
        backgroundColor: this.getColorForInjuryType(type, 0.1),
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 6,
      }));

      return {
        datasets,
      };
    },

    processStackedBarData() {
      const periodData = {};
      const injuryTypes = new Set();

      this.filteredInjuries.forEach((injury) => {
        const period = this.getPeriodKey(injury.created_date);
        const type = this.mapInjuryType(injury.injury_type);

        if (!periodData[period]) {
          periodData[period] = {};
        }
        if (!periodData[period][type]) {
          periodData[period][type] = 0;
        }
        periodData[period][type]++;
        injuryTypes.add(type);
      });

      const periods = Object.keys(periodData).sort();
      const labels = periods.map((period) => this.formatPeriodLabel(period));
      const datasets = Array.from(injuryTypes).map((type) => ({
        label: type,
        data: periods.map((period) => periodData[period][type] || 0),
        backgroundColor: this.getColorForInjuryType(type),
        borderColor: this.getColorForInjuryType(type),
        borderWidth: 1,
      }));

      return {
        labels,
        datasets,
      };
    },

    processTotalBarData() {
      const periodData = {};

      this.filteredInjuries.forEach((injury) => {
        const period = this.getPeriodKey(injury.created_date);
        if (!periodData[period]) {
          periodData[period] = 0;
        }
        periodData[period]++;
      });

      const periods = Object.keys(periodData).sort();
      const labels = periods.map((period) => this.formatPeriodLabel(period));
      const data = periods.map((period) => periodData[period] || 0);

      return {
        labels,
        datasets: [
          {
            label: "Total Injuries",
            data,
            backgroundColor: data.map((value) => {
              if (value === 0) return "rgba(108, 117, 125, 0.8)";
              if (value <= 2) return "rgba(40, 167, 69, 0.8)";
              if (value <= 4) return "rgba(255, 193, 7, 0.8)";
              return "rgba(220, 53, 69, 0.8)";
            }),
            borderColor: data.map((value) => {
              if (value === 0) return "rgba(108, 117, 125, 1)";
              if (value <= 2) return "rgba(40, 167, 69, 1)";
              if (value <= 4) return "rgba(255, 193, 7, 1)";
              return "rgba(220, 53, 69, 1)";
            }),
            borderWidth: 1,
          },
        ],
      };
    },

    processDoughnutData() {
      const injuryTypes = {};

      this.filteredInjuries.forEach((injury) => {
        const type = this.mapInjuryType(injury.injury_type);
        if (!injuryTypes[type]) {
          injuryTypes[type] = 0;
        }
        injuryTypes[type]++;
      });

      const labels = Object.keys(injuryTypes);
      const data = labels.map((type) => injuryTypes[type]);
      const backgroundColor = labels.map((type) =>
        this.getColorForInjuryType(type),
      );

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor,
            borderColor: backgroundColor.map((color) =>
              color.replace("0.8", "1"),
            ),
            borderWidth: 2,
          },
        ],
      };
    },

    formatPeriodLabel(period) {
      const date = new Date(period);
      switch (this.currentView) {
        case "day":
          return date.toLocaleDateString("en-GB", {
            month: "short",
            day: "numeric",
          });
        case "week":
          return `Week of ${date.toLocaleDateString("en-GB", { month: "short", day: "numeric" })}`;
        case "month":
          return date.toLocaleDateString("en-GB", {
            month: "short",
            year: "numeric",
          });
        default:
          return period;
      }
    },

    getPeriodKey(dateString) {
      const date = new Date(dateString);

      switch (this.currentView) {
        case "day":
          return date.toISOString().split("T")[0];
        case "week":
          return this.getWeekKey(dateString);
        case "month":
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        default:
          return this.getWeekKey(dateString);
      }
    },

    getWeekKey(dateString) {
      const date = new Date(dateString);
      const startOfWeek = new Date(
        date.setDate(date.getDate() - date.getDay()),
      );
      return startOfWeek.toISOString().split("T")[0];
    },

    mapInjuryType(injuryType) {
      if (!injuryType) return "Other";

      const injuryTypeGroups = [
        { label: "Ankle", values: ["Left ankle injury", "Right ankle injury"] },
        { label: "Knee", values: ["Left Knee injury", "Right Knee injury"] },
        { label: "Quad", values: ["Left Quad injury", "Right Quad injury"] },
        {
          label: "Hamstring",
          values: ["Left hamstring injury", "Right hamstring injury"],
        },
        { label: "Groin", values: ["Left groin injury", "Right groin injury"] },
        { label: "Hip", values: ["Left Hip injury", "Right Hip injury"] },
        {
          label: "Shoulder",
          values: ["Left Shoulder injury", "Right Shoulder injury"],
        },
        {
          label: "Wrist/Hand",
          values: ["Left Wrist and hand injury", "Right Wrist and hand injury"],
        },
        { label: "Head", values: ["Head injury"] },
        { label: "Foot", values: ["Left foot injury", "Right foot injury"] },
        { label: "Arm", values: ["Left arm injury", "Right arm injury"] },
        { label: "Calf", values: ["Left calf injury", "Right calf injury"] },
        { label: "Other", values: [] },
      ];

      for (const group of injuryTypeGroups) {
        if (
          group.values.some(
            (val) => val.toLowerCase() === injuryType.toLowerCase(),
          )
        ) {
          return group.label;
        }
      }
      return "Other";
    },

    getColorForInjuryType(type, opacity = 0.8) {
      const colors = {
        Ankle: `rgba(255, 99, 132, ${opacity})`,
        Knee: `rgba(54, 162, 235, ${opacity})`,
        Quad: `rgba(255, 205, 86, ${opacity})`,
        Hamstring: `rgba(75, 192, 192, ${opacity})`,
        Groin: `rgba(153, 102, 255, ${opacity})`,
        Hip: `rgba(255, 159, 64, ${opacity})`,
        Shoulder: `rgba(199, 199, 199, ${opacity})`,
        "Wrist/Hand": `rgba(83, 102, 255, ${opacity})`,
        Head: `rgba(255, 99, 255, ${opacity})`,
        Foot: `rgba(255, 159, 132, ${opacity})`,
        Arm: `rgba(54, 235, 162, ${opacity})`,
        Calf: `rgba(205, 255, 86, ${opacity})`,
        Other: `rgba(128, 128, 128, ${opacity})`,
      };
      return colors[type] || colors["Other"];
    },

    handleDrillDown(datasetIndex, dataIndex) {
      const dataset = this.chartData.datasets[datasetIndex];
      const point = dataset.data[dataIndex];

      this.$emit("drill-down", {
        injuryType: dataset.label,
        week: point.x,
        count: point.y,
        period: this.formatPeriodLabel(point.x),
      });
    },

    handleStackedBarDrillDown(datasetIndex, dataIndex) {
      const dataset = this.chartData.datasets[datasetIndex];
      const labels = this.chartData.labels;
      const count = dataset.data[dataIndex];
      const period = labels[dataIndex];

      this.$emit("drill-down", {
        injuryType: dataset.label,
        period: period,
        count: count,
        week: period, // For compatibility
      });
    },

    handleTotalBarDrillDown(dataIndex) {
      const labels = this.chartData.labels;
      const dataset = this.chartData.datasets[0];
      const count = dataset.data[dataIndex];
      const period = labels[dataIndex];

      this.$emit("drill-down", {
        injuryType: "All Types",
        period: period,
        count: count,
        week: period, // For compatibility
      });
    },

    handleDoughnutDrillDown(dataIndex) {
      const labels = this.chartData.labels;
      const dataset = this.chartData.datasets[0];
      const injuryType = labels[dataIndex];
      const count = dataset.data[dataIndex];

      this.$emit("drill-down", {
        injuryType: injuryType,
        period: "All Periods",
        count: count,
        week: "All Periods", // For compatibility
      });
    },
  },
};
</script>

<style scoped>
.injury-trend-chart-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chart-controls {
  margin-bottom: 20px;
}

.control-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 15px;
}

.control-row:last-child {
  margin-bottom: 0;
}

.view-controls,
.filter-controls,
.chart-type-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.view-controls label,
.filter-controls label,
.chart-type-controls label {
  font-weight: 600;
  color: #333;
  margin-right: 5px;
}

.view-btn,
.chart-type-btn {
  padding: 8px 16px;
  border: 2px solid #007bff;
  background: white;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.view-btn:hover,
.chart-type-btn:hover {
  background: #f8f9fa;
  transform: translateY(-1px);
}

.view-btn.active,
.chart-type-btn.active {
  background: #007bff;
  color: white;
}

.chart-type-btn {
  border-color: #28a745;
  color: #28a745;
}

.chart-type-btn.active {
  background: #28a745;
  color: white;
}

.filter-controls select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #333;
  font-size: 14px;
}

.filter-controls select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.chart-wrapper {
  position: relative;
  margin-bottom: 20px;
}

.chart-summary {
  border-top: 1px solid #eee;
  padding-top: 15px;
}

.summary-stats {
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.stat-item {
  text-align: center;
  min-width: 120px;
}

.stat-label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.stat-value {
  display: block;
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.trend-up {
  color: #dc3545;
}

.trend-down {
  color: #28a745;
}

.trend-stable {
  color: #6c757d;
}

/* Responsive Design */
@media (max-width: 768px) {
  .injury-trend-chart-container {
    padding: 15px;
  }

  .control-row {
    flex-direction: column;
    align-items: stretch;
  }

  .view-controls,
  .filter-controls,
  .chart-type-controls {
    justify-content: center;
  }

  .view-btn,
  .chart-type-btn {
    padding: 10px 20px;
    font-size: 16px;
  }

  .summary-stats {
    flex-direction: column;
    gap: 15px;
  }

  .stat-item {
    min-width: auto;
    width: 100%;
  }
}

@media (max-width: 480px) {

  .view-controls,
  .chart-type-controls {
    flex-direction: column;
    width: 100%;
  }

  .view-btn,
  .chart-type-btn {
    width: 100%;
    margin-bottom: 5px;
  }

  .filter-controls {
    width: 100%;
  }

  .filter-controls select {
    width: 100%;
  }
}

/* Accessibility */
.view-btn:focus,
.chart-type-btn:focus,
.filter-controls select:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* Loading state */
.chart-wrapper.loading {
  opacity: 0.6;
  pointer-events: none;
}

.chart-wrapper.loading::after {
  content: "Loading...";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.9);
  padding: 10px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* No data message */
.no-data-message {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.no-data-message p {
  margin: 0;
  font-size: 16px;
}
</style>
